/* eslint-disable class-methods-use-this */
const { deliveryTypes: DeliveryType, orderType, sourceAndDestinationType, chargeCode, sourceChargeCode } = require('pando-constants');
const { groupBy, uniqBy, uniq } = require('lodash');
const { freightUnit } = require('../../constants/payments');
const Helper = require('./utils/helper');
const convertWeight = require('../../helpers/weightConverter');
const convertVolume = require('../../helpers/volumeConverter');

class AdditionalPoint {
  getTotalPoints(data) {
    const isGateGroupingApplicable = this.gateGroupApplicable(data);
    const depots = groupBy(data, 'type')[sourceAndDestinationType.DEPOT];
    let depotsCount = depots ? depots.length : 0;

    const vendorDepots = groupBy(data, 'type')[sourceAndDestinationType.VENDOR_DEPOT];
    let vendorDepotsCount = vendorDepots ? vendorDepots.length : 0;
    if (isGateGroupingApplicable) {
      ({ depotsCount, vendorDepotsCount } = this.getModifiedCountAfterGrouping(depots, vendorDepots));
    }
    const consignees = groupBy(data, 'type')[sourceAndDestinationType.CONSIGNEE] || [];
    const { consigneesWithoutGroup, consigneesWithGroup } = consignees.reduce((acc, consignee) => {
      const _acc = acc;
      if (consignee.group) {
        _acc.consigneesWithGroup.push(consignee);
      } else {
        _acc.consigneesWithoutGroup.push(consignee);
      }
      return _acc;
    }, { consigneesWithoutGroup: [], consigneesWithGroup: [] });

    const consigneesByGroup = groupBy(consigneesWithGroup, 'group');
    const consigneesCount = (Object.keys(consigneesByGroup).length) + consigneesWithoutGroup.length;
    return depotsCount + consigneesCount + vendorDepotsCount;
  }

  getModifiedCountAfterGrouping(depots, vendorDepots) {
    const getCount = (items) => {
      if (items && items.length) {
        let totalCount = 0;
        const gateGroups = [];
        items.forEach(item => gateGroups.push(...item.gate_groups));
        const depotWiseGateGroups = {};
        gateGroups.forEach((gateGroup) => {
          if (gateGroup.location_id) {
            if (depotWiseGateGroups[gateGroup.location_id.toString()]) {
              depotWiseGateGroups[gateGroup.location_id.toString()].push(gateGroup.gate_group);
            } else {
              depotWiseGateGroups[gateGroup.location_id.toString()] = [gateGroup.gate_group];
            }
          }
        });
        for (const key in depotWiseGateGroups) {
          if ((depotWiseGateGroups[key] && depotWiseGateGroups[key].length)) {
            totalCount +=
            new Set(depotWiseGateGroups[key]).size;
          }
        }
        return totalCount;
      }
      return 0;
    };
    const depotsCount = getCount(depots);
    const vendorDepotsCount = getCount(vendorDepots);
    return { depotsCount, vendorDepotsCount };
  }

  gateGroupApplicable(data) {
    const gateGroups = (data && data.reduce((acc, point) => {
      const _acc = acc;
      if (point && point.gate_groups && point.gate_groups.length) {
        _acc.push(...point.gate_groups);
      }
      return _acc;
    }, [])) || [];
    return (gateGroups && gateGroups.length) ?
      gateGroups.every(gateGroup => gateGroup.gate_group &&
      gateGroup.gate_group !== '') : false;
  }

  calaculateQuantity(materialList, unitType, materialMap, quantityUnitsForPieces) {
    let resultQuantity = 0;
    if (materialList && materialList.length) {
      switch (unitType) {
        case freightUnit.per_ton: {
          resultQuantity = materialList.map(material => convertWeight(material.weightUnit, 't', material.weight))
            .reduce((acc, weight) => acc + weight, 0);
          break;
        }
        case freightUnit.per_kg: {
          resultQuantity = materialList.map(material => convertWeight(material.weightUnit, 'kg', material.weight))
            .reduce((acc, weight) => acc + weight, 0);
          break;
        }
        case freightUnit.per_cft: {
          resultQuantity = materialList.map(material => convertVolume(material.volumeUnit, 'cft', material.volume))
            .reduce((acc, volume) => acc + volume, 0);
          break;
        }
        case freightUnit.per_package:
        case freightUnit.per_case:
        case freightUnit.per_box: {
          resultQuantity = materialList.map((material) => {
            const quantity = (material.quantity) ? Math.ceil(material.quantity) : 0;
            if (quantityUnitsForPieces[material.quantityUnit.toLowerCase()]) {
              const materialDetail = materialMap[material.materialCode];
              return (materialDetail.pce_box) ?
                Math.ceil(quantity / Math.ceil(materialDetail.pce_box)) : 0;
            }
            return quantity;
          }).reduce((acc, quantity) => acc + quantity, 0);
          break;
        }
        default:
          resultQuantity = 0;
          break;
      }
    }
    return resultQuantity;
  }

  dropPointCharge(data, maxForwardFreightCharge, extraDropOffCharges, forwardContracts) {
    let dropPointCharge = 0;
    const destinationIds = uniq(forwardContracts.map(contract => contract.destinationId.toString()));
    const forwardDrops = data.destinations.filter(destination => destinationIds.includes(destination.locationId.toString()));
    const noOfDestinations = forwardDrops ? this.getTotalPoints(forwardDrops) : 1;
    const noOfAdditionalPoints = (noOfDestinations > 1) ? (noOfDestinations - 1) : 0;
    if (maxForwardFreightCharge && maxForwardFreightCharge.pointCharges) {
      dropPointCharge = maxForwardFreightCharge.pointCharges * noOfAdditionalPoints;
    } else if (extraDropOffCharges && extraDropOffCharges.drop_off_charge) {
      dropPointCharge = noOfAdditionalPoints * extraDropOffCharges.drop_off_charge;
    }
    return dropPointCharge;
  }

  returnDropPointCharge(data, maxReturnFreightCharge, returnContracts) {
    let dropPointCharge = 0;
    const destinationIds = data.destinations.map(destination => destination.locationId.toString());
    const returnDrops = returnContracts.filter(contract => destinationIds.includes(contract.destinationId.toString()));
    const filterGateId = returnDrops.filter(contract => contract.destinationGateId !== undefined);
    const gateIds = uniq(filterGateId.map(contract => contract.destinationGateId.toString()));
    const noOfDestinations = gateIds.length;
    const noOfAdditionalPoints = (noOfDestinations > 1) ? (noOfDestinations - 1) : 0;
    if (maxReturnFreightCharge && maxReturnFreightCharge.returnDropPointCharges) {
      dropPointCharge = maxReturnFreightCharge.returnDropPointCharges * noOfAdditionalPoints;
    }
    return dropPointCharge;
  }

  pickupPointCharge(data, maxFreightCharge, contracts, chargeName) {
    let pickupPointCharge = 0;
    const sourceIds = uniq(contracts.map(contract => contract.sourceId.toString()));
    const pickups = data.indentSources.filter(source => sourceIds.includes(source.location_id.toString()));
    const noOfPickups = pickups ? this.getTotalPoints(pickups) : 1;
    const noOfAdditionalPoints = (noOfPickups > 1) ? (noOfPickups - 1) : 0;
    if (maxFreightCharge && maxFreightCharge[chargeName]) {
      pickupPointCharge = maxFreightCharge[chargeName] * noOfAdditionalPoints;
    }
    return pickupPointCharge;
  }

  getDestinationDropCharge(data, destinationCharges, quantityUnitsForPieces) {
    let dropCharge = 0;
    const freightContracts = data.freightCharges;
    const materialMap = Helper.fetchMaterialMap(data.materials.masterData);
    const gateIds = [...new Set(freightContracts.filter(contract => contract.sourceGateId)
      .map(contract => contract.sourceGateId.toString()))];

    data.destinations.forEach((destination) => {
      const destinationCharge = destinationCharges && destinationCharges.find(charge =>
        charge.destination_id.toString() === destination.locationId.toString());
      if (gateIds.length) {
        gateIds.forEach((gateId) => {
          const contractCharge = freightContracts.find((contract) => {
            if (contract.destinationGateId) {
              return (contract.sourceGateId.toString() === gateId &&
                  contract.destinationId.toString() === destination.locationId.toString() &&
                  contract.destinationGateId.toString() === destination.destinationGateId.toString());
            }
            return (contract.sourceGateId.toString() === gateId &&
                  contract.destinationId.toString() === destination.locationId.toString());
          });
          if (destinationCharge && contractCharge) {
            const [chargeObj] = destinationCharge.charges;

            const amount = (chargeObj.charge_unit === freightUnit.per_trip) ? chargeObj.charge
              : (this.calaculateQuantity(Helper.getNormalizedMaterialDetails(destination.materials),
                chargeObj.charge_unit, materialMap, quantityUnitsForPieces) * chargeObj.charge);
            dropCharge += Math.max(amount, chargeObj.min_charge);
          }
        });
      } else {
        const contractCharge = freightContracts.find(contract =>
          contract.destinationId.toString() === destination.locationId.toString());
        if (destinationCharge && contractCharge) {
          const [chargeObj] = destinationCharge.charges;
          const amount = (chargeObj.charge_unit === freightUnit.per_trip) ? chargeObj.charge
            : (this.calaculateQuantity(Helper.getNormalizedMaterialDetails(destination.materials),
              chargeObj.charge_unit, materialMap, quantityUnitsForPieces) * chargeObj.charge);
          dropCharge += Math.max(amount, chargeObj.min_charge);
        }
      }
    });
    return dropCharge;
  }

  computePickupCharge(sources, materialMap, quantityUnitsForPieces,
    materials, gateData, sourcePickupPointCharges) {
    let pickupCharge = 0;
    sources.forEach((source) => {
      const sourceMaterials = materials.filter(material => material.source_location_id.toString() === source.location_id.toString());
      const sourceCharge = sourcePickupPointCharges.find((charge) => {
        const isLocationIdEqual = charge.source_location_id.toString() === source.location_id.toString();
        let isGateIdEqual = true;
        if (gateData.length) {
          isGateIdEqual = charge.source_gate_id.toString() === source.gate_id.toString();
        }
        return isLocationIdEqual && isGateIdEqual;
      });

      if (sourceCharge) {
        const [chargeObj] = sourceCharge.charges;
        const materialList = !gateData.length ? sourceMaterials : sourceMaterials.filter(material =>
          material.source_gate_id.toString() === source.gate_id.toString());

        const amount = (chargeObj.charge_unit === freightUnit.per_trip) ? chargeObj.charge
          : (this.calaculateQuantity(Helper.getNormalizedMaterialDetails(materialList),
            chargeObj.charge_unit, materialMap, quantityUnitsForPieces) * chargeObj.charge);

        pickupCharge += Math.max(amount, chargeObj.min_charge);
      }
    });

    return pickupCharge;
  }

  getSourcePickupCharge(data, sourcePickupPointCharges, quantityUnitsForPieces) {
    let pickupCharge = 0;
    if (sourcePickupPointCharges && sourcePickupPointCharges.length) {
      const gateData = sourcePickupPointCharges.filter(charge => charge.source_gate_id);
      const materialMap = Helper.fetchMaterialMap(data.materials.masterData);

      let sourceByDestination;
      let materialList;
      if (data.deliveryType === DeliveryType.PTL) {
        const destination = data.destinations[0];
        const destinationContracts = [...new Set(data.freightCharges.filter(contract =>
          (contract.destinationId.toString() === destination.locationId.toString())
          && contract.sourceGateId))];
        const sourceGateIds = destinationContracts.map(contract => contract.sourceGateId.toString());

        sourceByDestination = data.indentSources.filter((source) => {
          if (sourceGateIds.length) {
            return sourceGateIds.includes(source.gate_id.toString());
          }
          return destination.sourceLocationId.toString() === source.location_id.toString();
        });
        materialList = destination.materials;
      } else {
        sourceByDestination = data.indentSources;
        materialList = data.allDestinations.reduce((acc, destination) => {
          if (destination.materials) {
            return acc.concat(destination.materials);
          }
          return acc;
        }, []);
      }

      if (!gateData.length) {
        sourceByDestination = uniqBy(sourceByDestination,
          _source => _source.location_id.toString());
      }
      pickupCharge = this.computePickupCharge(sourceByDestination, materialMap,
        quantityUnitsForPieces, materialList, gateData, sourcePickupPointCharges);
    }
    return pickupCharge;
  }

  pointCharges(data) {
    const quantityUnitsForPieces = Helper
      .getQuantityUnitsForPieces(data.customQuantityUnits);
    const deliveryType = data.deliveryType;
    const extraDropOffCharges = data.extraDropOffCharges;
    let dropCharge = 0;
    let returnDropCharge = 0;
    let pickupCharge = 0;
    let returnPickupCharge = 0;
    let destinationDropCharge = 0;
    let sourcePickupCharge = 0;

    // If customer enable both DROP_POINT and EXTRA_DROP_OFF then this will give wrong calculation.
    const destinationDropPointCharges = data.indentDestinationCharges && data.indentDestinationCharges.filter(charge =>
      (charge.charge_code === chargeCode.DROP_POINT ||
        charge.charge_code === chargeCode.EXTRA_DROP_OFF));
    const sourcePickupPointCharges = data.indentSourceCharges && data.indentSourceCharges.filter(charge =>
      charge.charge_code === sourceChargeCode.ADDITIONAL_PICKUP);

    if (data.orderType === orderType.CONTRACT) {
      const forwardContracts = data.freightCharges;
      const maxForwardFreightCharge = forwardContracts.length ? forwardContracts.reduce((acc, value) =>
        (acc.freightCharge > value.freightCharge ? acc : value)) : {};

      const returnContracts = data.returnFreightCharges;
      const maxReturnFreightCharge = returnContracts.length ? returnContracts.reduce((acc, value) =>
        (acc.returnFreightRate > value.returnFreightRate ? acc : value)) : {};

      dropCharge = this.dropPointCharge(data, maxForwardFreightCharge, extraDropOffCharges, forwardContracts);
      returnDropCharge = this.returnDropPointCharge(data, maxReturnFreightCharge, returnContracts);
      destinationDropCharge = this.getDestinationDropCharge(data, destinationDropPointCharges, quantityUnitsForPieces);
      sourcePickupCharge = this.getSourcePickupCharge(data, sourcePickupPointCharges, quantityUnitsForPieces);
      if (deliveryType === DeliveryType.FTL) {
        pickupCharge = this.pickupPointCharge(data, maxForwardFreightCharge, forwardContracts, 'pickupPointCharges');
        returnPickupCharge = this.pickupPointCharge(data, maxReturnFreightCharge, returnContracts, 'returnPickupPointCharges');
      }
    }

    return (pickupCharge + dropCharge + destinationDropCharge
      + sourcePickupCharge + returnDropCharge + returnPickupCharge);
  }
}
module.exports = AdditionalPoint;
